/**
 * Shared modal focus-trap hook (extracted from VideoSelectorModal /
 * ImageInsertModal, DUP-5).
 *
 * Owns the full keyboard/focus lifecycle for a modal dialog:
 * - Captures the previously focused element when the modal opens and
 *   restores focus to it when the modal closes.
 * - Moves initial focus to the first focusable element inside the modal,
 *   falling back to the modal container itself.
 * - Cycles Tab / Shift+Tab focus within the modal.
 * - Calls `onClose` on Escape (unconditionally — gate `onClose` itself if a
 *   modal must block closing while busy).
 *
 * The component calling this hook must stay mounted while the modal is
 * closed (render `null` instead of unmounting) so the close transition can
 * restore focus. `ModalShell` handles this automatically.
 *
 * New or refactored dialogs should use this hook (directly, or via
 * `ModalShell`) instead of hand-rolling focus-trap logic.
 */

import { useCallback, useEffect, useRef } from 'react';
import type { KeyboardEvent, RefObject } from 'react';

function getFocusableElements(container: HTMLElement): HTMLElement[] {
  const focusableSelectors = [
    'button:not([disabled])',
    'input:not([disabled])',
    'textarea:not([disabled])',
    'select:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
    'a[href]',
  ].join(', ');

  return Array.from(container.querySelectorAll<HTMLElement>(focusableSelectors));
}

/** True when `a` comes before `b` in document order (an ancestor comes before its descendants). */
function comesBefore(a: Node, b: Node): boolean {
  return a !== b && Boolean(a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING);
}

export interface UseModalFocusTrapOptions {
  isOpen: boolean;
  onClose: () => void;
}

export interface UseModalFocusTrapResult {
  /** Attach to the modal container element (give it `tabIndex={-1}`). */
  modalRef: RefObject<HTMLDivElement | null>;
  /** Attach to the dialog overlay (or any ancestor of the modal container). */
  handleKeyDown: (e: KeyboardEvent) => void;
}

export function useModalFocusTrap({
  isOpen,
  onClose,
}: UseModalFocusTrapOptions): UseModalFocusTrapResult {
  const modalRef = useRef<HTMLDivElement>(null);
  const previousActiveElement = useRef<HTMLElement | null>(null);

  // Initial focus on open; restore previous focus on close.
  useEffect(() => {
    if (isOpen) {
      previousActiveElement.current = document.activeElement as HTMLElement;
      if (modalRef.current) {
        const focusable = getFocusableElements(modalRef.current);
        const first = focusable[0];
        if (first) {
          first.focus();
        } else {
          modalRef.current.focus();
        }
      }
    } else {
      previousActiveElement.current?.focus();
    }
  }, [isOpen]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
        return;
      }

      if (e.key === 'Tab' && modalRef.current) {
        const focusable = getFocusableElements(modalRef.current);
        if (focusable.length === 0) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        const active = document.activeElement;
        if (!first || !last || !active) return;

        // Wrap whenever nothing tabbable lies ahead in the dialog, not only from
        // the first or last control: focus can also sit on an element outside
        // the list (the dialog itself, or a tabIndex -1 player wrapper), and
        // the browser would then move it out of the dialog.
        if (e.shiftKey) {
          if (!focusable.some((element) => comesBefore(element, active))) {
            e.preventDefault();
            last.focus();
          }
        } else {
          if (!focusable.some((element) => comesBefore(active, element))) {
            e.preventDefault();
            first.focus();
          }
        }
      }
    },
    [onClose]
  );

  return { modalRef, handleKeyDown };
}
